/**
 * Gates site NPI validation for the current single order.
 *
 * @module useSiteNpiValidated
 */
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulator/DataAccumulatorContext";
import { isHealthFacilityFeatureEnabled } from "../../../data-model/healthFacility";
import { RootState } from "../../../redux/store/store";
import { isValidString } from "../../../utils/stringModifications";

/**
 * Reports whether the current order still needs a site NPI type.
 *
 * Validation is needed only when the facility has `is_site_npi_required`
 * enabled and `status.npi_type` is missing or blank.
 *
 * @returns `isValidationNeeded` — true only for an enabled facility with an invalid type.
 */
export const useSiteNpiValidated = () => {
  const { generalOrderDetails } = useDataAccumulatorForSingleOrder();
  const currentFacility = useSelector(
    (state: RootState) => state.singleHealthFacilityDetails.data,
  );

  const isSiteNpiValidationConfiguredInHealthCareFacilityLevel = useMemo(() => {
    return isHealthFacilityFeatureEnabled(
      currentFacility,
      "is_site_npi_required",
    );
  }, [currentFacility]);

  const isSiteNpiInOrder = useMemo(() => {
    return isValidString(generalOrderDetails?.order?.status?.npi_type);
  }, [generalOrderDetails]);

  const isValidationNeeded = useMemo(() => {
    return (
      isSiteNpiValidationConfiguredInHealthCareFacilityLevel &&
      !isSiteNpiInOrder
    );
  }, [
    isSiteNpiValidationConfiguredInHealthCareFacilityLevel,
    isSiteNpiInOrder,
  ]);

  return {
    isValidationNeeded,
  };
};
